import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import { getFieldValue } from '@sitecore-jss/sitecore-jss-react';
import { useSpring, animated } from 'react-spring';
import { Button } from 'components/components/atoms';
import SubNavigationContactModal from 'components/components/molecules/SubNavigationContactModal';
import { useUiValue, openRegistrationModal } from 'stores/uiContext';
import useBreakpoint from 'utils/hooks/useBreakpoint';
import useScrollDirection from 'utils/hooks/useScrollDirection';
import { getDefaultFontSize } from 'utils/getDefaultFontSize';

const StyledButton = styled(Button)`
  padding-top: 0;
  padding-bottom: 0;
  padding-left: 0.75rem;
  padding-right: 0.75rem;
  height: 2.75rem;
  min-width: 0;
  width: 100%;
  border-radius: 0.5rem;
`;

const getFieldOrValue = (fields, fieldName) => {
  const fieldValue = getFieldValue(fields, fieldName);
  if (fieldValue) return fieldValue;

  const rawValue = fields[fieldName];
  return rawValue && rawValue.value ? rawValue.value : rawValue || '';
};

const AnchorCTA = ({ fields }) => {
  const firstAddress = getFieldValue(fields, 'firstAddress');
  const secondAddress = getFieldValue(fields, 'secondAddress');
  const latitude = getFieldValue(fields, 'latitude');
  const longitude = getFieldValue(fields, 'longitude');
  const state = getFieldOrValue(fields, 'state');
  const city = getFieldOrValue(fields, 'city');
  const zipcode = getFieldOrValue(fields, 'zipcode');
  const phoneNumberCall = getFieldValue(fields, 'phoneNumberCall');
  const openNow = getFieldValue(fields, 'openNow');
  const schedule = getFieldValue(fields, 'schedule');
  const gmbLink = getFieldValue(fields, 'gmbLink') || fields.gmbLink || {};
  const [
    {
      notificationActive,
      notificationHeight,
      nationalNotificationHeight,
      subNavigationBarGeneralData,
    },
    dispatch,
  ] = useUiValue();
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  const [fixedBarVisible, setFixedBarVisible] = useState(false);
  const [defaultFontSize, setDefaultFontSize] = useState();
  const [notificationTopHeight, setNotificationTopHeight] = useState(0);
  const breakpoint = useBreakpoint(
    typeof window !== 'undefined' && window.innerWidth,
  );
  const isMobile = ['sm', 'xs', 'md'].includes(breakpoint);
  const scrollDirection = useScrollDirection();
  const title =
    subNavigationBarGeneralData && subNavigationBarGeneralData.title;
  const buttonCta =
    subNavigationBarGeneralData && subNavigationBarGeneralData.buttonCta;
  const ctaText = buttonCta && buttonCta.text ? buttonCta.text : 'Get Info';
  const salesCenter = {
    address1: firstAddress,
    address2: secondAddress,
    city,
    stateAbbreviation: state,
    zipcode,
    phoneNumberCall,
    gmbLink,
    latitude,
    longitude,
    openNow,
    schedule,
  };

  const handleCTAClick = () => {
    window.dataLayer.push({ event: 'email' });
    dispatch(openRegistrationModal());
  };

  useEffect(() => {
    const font = getDefaultFontSize();
    setDefaultFontSize(font);
  }, []);

  useEffect(() => {
    setNotificationTopHeight(notificationHeight + nationalNotificationHeight);
  }, [notificationHeight, nationalNotificationHeight]);

  const fixedBarStyle = useSpring({
    config: { duration: 100 },
    top: fixedBarVisible
      ? scrollDirection === 'up'
        ? defaultFontSize <= 18
          ? notificationTopHeight + 84 // 80
          : notificationTopHeight + 150
        : defaultFontSize <= 18
        ? notificationTopHeight
        : notificationTopHeight + 70
      : -110, // 80
    backgroundColor: '#fff',
  });

  const handleOnScroll = useCallback(() => {
    if (!isMobile) {
      return;
    }

    const currentScrollY = window.scrollY;
    const offset = notificationActive ? 360 : 300;
    if (currentScrollY > offset) {
      setFixedBarVisible(true);
    } else if (fixedBarVisible) {
      setFixedBarVisible(false);
    }
  }, [fixedBarVisible, isMobile, notificationActive]);

  useEffect(() => {
    window.addEventListener('scroll', handleOnScroll);
    return () => window.removeEventListener('scroll', handleOnScroll);
  }, [handleOnScroll]);

  useEffect(() => {
    const chatBoxDiv = document.getElementById('drift-frame-controller');
    if (chatBoxDiv) {
      chatBoxDiv.style.bottom = '';
    }
  });

  return (
    <>
      <SubNavigationContactModal
        isOpen={isContactModalOpen}
        onClose={() => setIsContactModalOpen(false)}
        salesCenter={salesCenter}
        communityTitle={title}
      />
      <animated.div
        id='anchor-cta'
        className='block lg:hidden md:hidden fixed w-screen top-0 left-0 z-30 bg-white shadow-md px-5 pt-5 pb-3'
        style={fixedBarStyle}
        aria-hidden={!fixedBarVisible}
      >
        <div className='text-left'>
          {title && (
            <div className='text-mattamy-blue font-trade-gothic-20 leading-none tracking-tighter text-xl'>
              {title}
            </div>
          )}
        </div>
        <div className='mt-2 grid grid-cols-2 gap-2'>
          <StyledButton
            color='mattamy-blue'
            textColor='mattamy-blue'
            variant='outlined'
            onClick={() => setIsContactModalOpen(true)}
            smallCTA
            role='link'
          >
            Contact Us
          </StyledButton>
          <StyledButton
            color='action-blue'
            textColor='white'
            onClick={handleCTAClick}
            smallCTA
            role='link'
          >
            {ctaText}
          </StyledButton>
        </div>
      </animated.div>
    </>
  );
};

export default AnchorCTA;
